import { useState, useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { FilterState } from '@/types/api';

interface UseTweetsOptions {
  pageSize?: number;
}

export function useTweets(options: UseTweetsOptions = {}) {
  const { pageSize = 20 } = options;

  const [filters, setFilters] = useState<FilterState>({
    type: 'all',
    limit: pageSize,
    search: '',
    author: '',
    hasMedia: false,
  });

  const infiniteQuery = useInfiniteQuery({
    queryKey: ['tweets', filters],
    queryFn: ({ pageParam = 1 }) => api.getTweets(pageParam as number, pageSize, filters),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.tweets.length >= pageSize ? allPages.length + 1 : undefined;
    },
  });

  const tweets = infiniteQuery.data?.pages.flatMap((page) => page.tweets) ?? [];
  const total = infiniteQuery.data?.pages[0]?.pagination.total ?? 0;

  const loadTweets = useCallback(() => infiniteQuery.refetch(), [infiniteQuery]);
  const loadMoreTweets = useCallback(() => {
    if (infiniteQuery.hasNextPage && !infiniteQuery.isFetchingNextPage) {
      infiniteQuery.fetchNextPage();
    }
  }, [infiniteQuery]);

  const updateFilters = useCallback((newFilters: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      type: 'all',
      limit: pageSize,
      search: '',
      author: '',
      hasMedia: false,
    });
  }, [pageSize]);

  return {
    tweets,
    loading: infiniteQuery.isLoading || infiniteQuery.isFetchingNextPage,
    hasMore: infiniteQuery.hasNextPage ?? false,
    total,
    filters,
    loadTweets,
    loadMoreTweets,
    updateFilters,
    resetFilters,
  };
}
